import { useEffect, useRef, useMemo } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { useEmailThread } from '@/api/communications/email-threads/useEmailThread';
import { useActivityGraphContext } from '../ActivityGraphContext';
import { DocumentItem, DocumentItemData } from './DocumentItem';

interface DocumentsTabProps {
  conversationId?: string | null;
}

export function DocumentsTab({ conversationId }: DocumentsTabProps) {
  const context = useActivityGraphContext();
  const {
    data: threadData,
    isLoading,
    error,
    refetch,
  } = useEmailThread(conversationId ?? null);
  const documentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const expandedDocumentId = context?.expandedDocumentId ?? null;
  const highlightedDocumentId = context?.highlightedDocumentId ?? null;

  // Aggregate all documents from all emails in the thread, with deduplication.
  // The same file can appear in both documentAssociations (newer) and
  // attachments (legacy), or across multiple emails in the thread.
  const allDocuments = useMemo(() => {
    if (!threadData?.emails) return [];

    const documents: DocumentItemData[] = [];
    const seenDocumentIds = new Set<string>();
    const seenFileNames = new Set<string>();

    for (const email of threadData.emails) {
      // Add document associations (preferred — richer data, signed URLs)
      if (email.documentAssociations) {
        for (const assoc of email.documentAssociations) {
          // Skip inline images: only filter out image/* types with a contentId.
          // Some email clients (e.g. Outlook) set Content-ID on all MIME parts,
          // including regular file attachments like Excel/PDF, so checking
          // contentId alone would incorrectly hide non-image attachments.
          if (
            assoc.document.contentId &&
            assoc.document.mimeType?.startsWith('image/')
          )
            continue;

          // Deduplicate by document ID or filename
          if (seenDocumentIds.has(assoc.document.id)) continue;
          const fileName =
            assoc.document.originalFileName || assoc.document.name;
          if (seenFileNames.has(fileName.toLowerCase())) continue;

          seenDocumentIds.add(assoc.document.id);
          seenFileNames.add(fileName.toLowerCase());

          documents.push({
            id: assoc.id,
            documentId: assoc.document.id,
            fileName,
            sizeBytes: assoc.document.fileSizeBytes || 0,
            mimeType: assoc.document.mimeType,
            s3Url: assoc.document.url,
            sourceEmailSubject: email.subject ?? undefined,
          });
        }
      }

      // Add legacy attachments, skipping any already covered by document associations
      if (email.attachments) {
        for (const att of email.attachments) {
          if (seenFileNames.has(att.fileName.toLowerCase())) continue;
          seenFileNames.add(att.fileName.toLowerCase());

          documents.push({
            id: att.id,
            fileName: att.fileName,
            sizeBytes: att.sizeBytes,
            mimeType: att.mimeType,
            s3Url: att.s3Url,
            sourceEmailSubject: email.subject ?? undefined,
          });
        }
      }
    }

    return documents;
  }, [threadData?.emails]);

  // Auto-expand the first document when the list first loads.
  // Track per-conversation so the guard resets when navigating between drafts.
  const autoExpandedConversationId = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId) return;
    if (
      allDocuments.length > 0 &&
      autoExpandedConversationId.current !== conversationId &&
      !expandedDocumentId
    ) {
      autoExpandedConversationId.current = conversationId;
      const firstDocKey = allDocuments[0].documentId || allDocuments[0].id;
      context?.toggleDocumentExpanded(firstDocKey);
    }
  }, [allDocuments, expandedDocumentId, context, conversationId]);

  // Scroll to highlighted document
  useEffect(() => {
    if (highlightedDocumentId) {
      // Find the document by either documentId or id
      const docKey =
        allDocuments.find(
          (d) =>
            d.documentId === highlightedDocumentId ||
            d.id === highlightedDocumentId,
        )?.documentId ||
        allDocuments.find((d) => d.id === highlightedDocumentId)?.id;

      if (docKey && documentRefs.current.has(docKey)) {
        const element = documentRefs.current.get(docKey);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const timer = setTimeout(() => {
          context?.clearHighlights();
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [highlightedDocumentId, allDocuments, context]);

  const handleToggleExpanded = (documentId: string) => {
    context?.toggleDocumentExpanded(documentId);
  };

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <T>No documents available</T>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        <T>Error loading documents</T>
      </div>
    );
  }

  if (allDocuments.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <T>No documents attached to this conversation</T>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      <div className="text-sm text-muted-foreground mb-3">
        {allDocuments.length !== 1 ? (
          <T context="count of documents in thread (plural)">
            <Var>{allDocuments.length}</Var> documents
          </T>
        ) : (
          <T context="count of documents in thread (singular)">
            <Var>{allDocuments.length}</Var> document
          </T>
        )}
      </div>
      {allDocuments.map((doc) => {
        const docKey = doc.documentId || doc.id;
        const isExpanded = expandedDocumentId === docKey;
        const isHighlighted = highlightedDocumentId === docKey;

        return (
          <div
            key={doc.id}
            ref={(el) => {
              if (el) documentRefs.current.set(docKey, el);
            }}
          >
            <DocumentItem
              document={doc}
              isExpanded={isExpanded}
              isHighlighted={isHighlighted}
              onToggleExpanded={() => handleToggleExpanded(docKey)}
              onRefreshData={() => refetch()}
            />
          </div>
        );
      })}
    </div>
  );
}
